import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Footer = () => {
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/categories`).then((r) => setCategories(r.data)).catch(() => setCategories([]));
    axios.get(`${API_URL}/content`).then((r) => setContent(r.data)).catch(() => setContent(null));
  }, []);

  const contact = content?.contact || {};

  return (
    <footer className="bg-gray-900 text-white mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img
                src="https://customer-assets.emergentagent.com/job_cozy-sleepwear-8/artifacts/lmyiwfsg_logo-vshoppe.jpeg"
                alt="VShoppe"
                className="h-12 w-auto rounded-lg bg-white p-1"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Your trusted online store in New Zealand for maternity, sleepwear, nursing & baby essentials.
            </p>
            <div className="flex gap-4 pt-2">
              {contact.facebook && (
                <a href={contact.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook" data-testid="footer-facebook">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {contact.instagram && (
                <a href={contact.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram" data-testid="footer-instagram">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {contact.twitter && (
                <a href={contact.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Twitter" data-testid="footer-twitter">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Shop - dynamic categories */}
          <div>
            <h3 className="text-white font-semibold mb-4">Shop</h3>
            <ul className="space-y-3" data-testid="footer-shop-list">
              <li>
                <Link to="/shop" className="text-gray-400 hover:text-white transition-colors text-sm">
                  All Products
                </Link>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link to={`/shop?category=${encodeURIComponent(cat.name)}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-white font-semibold mb-4">Support</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/shipping" className="text-gray-400 hover:text-white transition-colors text-sm" data-testid="footer-shipping-link">
                  Shipping Info
                </Link>
              </li>
              <li>
                <Link to="/returns" className="text-gray-400 hover:text-white transition-colors text-sm" data-testid="footer-returns-link">
                  Returns & Exchanges
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-gray-400 hover:text-white transition-colors text-sm" data-testid="footer-reviews-link">
                  Reviews
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-3" data-testid="footer-contact">
              {contact.address && (
                <li className="flex items-start gap-3 text-gray-400 text-sm">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{contact.address}</span>
                </li>
              )}
              {contact.phone && (
                <li className="flex items-center gap-3 text-gray-400 text-sm">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <a href={`tel:${contact.phone}`} className="hover:text-white transition-colors">{contact.phone}</a>
                </li>
              )}
              {contact.email && (
                <li className="flex items-center gap-3 text-gray-400 text-sm">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <a href={`mailto:${contact.email}`} className="hover:text-white transition-colors" data-testid="footer-email">{contact.email}</a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} VShoppe. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-300 transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-300 transition-colors text-sm">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
